import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, share, take, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { TripService } from '~app/services';
import { AuthService } from '~app/services/auth.service';
import { UserGroupService } from '~app/services/user-group.service';
import { CiaoSharedModule } from '~app/components/shared/shared.module';
import { CiaoFormsModule } from '~app/components/shared/forms/forms.module';
import { RolePermissionService } from '~app/services/role-permission.service';
import { addStartEndDateCrossValidation } from '../trip-page-validators';
import { TripAttributes } from '~app/models';
@Component({
    selector: 'ciao-new-trip',
    imports: [CommonModule, CiaoSharedModule, CiaoFormsModule],
    templateUrl: './new-trip.component.html',
    styleUrl: './new-trip.component.less'
})
export class NewTripComponent {
  constructor(
    private readonly tripService: TripService,
    private readonly userGroupService: UserGroupService,
    private readonly rolePermissionService: RolePermissionService,
    private readonly authService: AuthService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location
  ) {}

  duplicating: boolean = false;
  oldTripId: string = null;
  subscriptions = new Subscription();
  formGroup = new FormGroup({
    userGroupId: new FormControl('', Validators.required),
    startDate: new FormControl(new Date().toISOString(), Validators.required),
    endDate: new FormControl('', Validators.required),
  });

  readonly userGroupOptions$ = this.rolePermissionService
    .searchMyUserGroupsByPermission({
      permissionIds: ['create_trips', 'edit_trip'],
    })
    .pipe(
      map((userGroups) =>
        userGroups.filter((group) => group.tierLabel === 'Team')
      ),
      this.userGroupService.op_sortGroups,
      map((userGroups) =>
        userGroups.map((userGroup) => ({
          value: userGroup.id,
          label: `${userGroup.labelPrefix}${userGroup.label}`,
        }))
      ),
      share()
    );

  get canCreate() {
    return this.formGroup.valid;
  }
  ngOnInit() {
    this.duplicating = this.activatedRoute.snapshot.data.duplicateTrip;
    this.oldTripId = this.activatedRoute.snapshot.params.id;
    addStartEndDateCrossValidation(
      this.formGroup.controls.startDate,
      this.formGroup.controls.endDate
    );
    this.forceTeamSelectIfDuplicating();
  }

  Cancel() {
    this.location.back();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  forceTeamSelectIfDuplicating() {
    if (this.duplicating) {
      this.formGroup.controls.userGroupId.disable();
      this.tripService
        .findById(this.oldTripId)
        .pipe(
          take(1),
          map((trip) => trip.userGroupId),
          tap((userGroupId) => this.formGroup.patchValue({ userGroupId }))
        )
        .subscribe();
    } else {
      this.formGroup.controls.userGroupId.enable();
      return;
    }
  }

  async CreateTrip() {
    if (this.formGroup.invalid) {
      this.toastrService.error('Cannot create Trip.  Form invalid.');
    }

    let save$: Observable<TripAttributes>;
    let successMessage = 'Trip Created Successfully';
    const tripData = {
      userGroupId: this.formGroup.value.userGroupId,
      startDate: this.formGroup.value.startDate,
      endDate: this.formGroup.value.endDate,
    };
    if (this.activatedRoute.snapshot.data.duplicateTrip) {
      save$ = this.tripService.duplicateTrip({
        tripId: this.activatedRoute.snapshot.params.id,
        ...tripData,
      });
      successMessage = 'Trip Duplicated Successfully';
    } else {
      // Do NOT pre-add the current user as a crew member here. Guest is a
      // system-level role, so a dispatcher creating a trip also reads as a guest
      // by the global isGuest$ check — adding them as crew then fails server-side
      // ("Dispatchers cannot be assigned as a Crew Member"). The backend decides
      // per-team whether to auto-add the creator as crew (only true guests on
      // the trip's team), so just send the trip data and let it handle this.
      save$ = this.tripService.saveTrip({ ...tripData });
    }
    this.subscriptions.add(
      save$
        .pipe(
          tap({
            next: (trip) => {
              this.toastrService.success(successMessage);
              this.router.navigate(['/trip', 'id', trip.id]);
            },
            error: (err) => {
              const errorBlob = JSON.stringify(err?.error ?? err ?? '');
              const isDispatcherCrewError =
                /Dispatcher.*cannot be assigned.*Crew Member/i.test(errorBlob);
              const isGuestPocError =
                /Guests cannot be assigned.*POC/i.test(errorBlob);
              let message: string;
              if (isDispatcherCrewError) {
                message =
                  'Dispatchers cannot be assigned as a Crew Member, POC, or Additional POC on a trip.';
              } else if (isGuestPocError) {
                message =
                  'Guests cannot be assigned as a POC or Additional POC on a trip.';
              } else {
                message = err.message;
              }
              this.toastrService.error(message, 'Failed to Create Trip');
            },
          }),
          catchError((err) => of(null))
        )
        .subscribe()
    );
  }
}
